import React, { useState, useEffect, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  addDoc,
  collection,
  query,
  onSnapshot,
  getDoc,
  deleteDoc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// ==========================================
// MODULE 1: CONFIGURATION & MOCK DATA
// ==========================================

const COUNTRIES = [
  { code: 'US', name: 'United States', idHint: 'National Provider Identifier (NPI)' },
  { code: 'IN', name: 'India', idHint: 'National Medical Commission (NMC) / MCI Number' },
  { code: 'SA', name: 'Kingdom of Saudi Arabia', idHint: 'SCFHS Professional Registration ID' },
  { code: 'AE', name: 'United Arab Emirates', idHint: 'DHA / MOH License Number' },
  { code: 'TR', name: 'Turkey', idHint: 'Ministry of Health (Sağlık Bakanlığı) Dip. No' },
  { code: 'PT', name: 'Portugal', idHint: 'Cédula Profissional (Ordem dos Médicos)' },
  { code: 'SG', name: 'Singapore', idHint: 'Singapore Medical Council (SMC) Reg. Number' },
  { code: 'TH', name: 'Thailand', idHint: 'Medical Council of Thailand License No' }
];

export const getCountryCodeLabel = (countryCode) => {
  const normalized = (countryCode || '').toUpperCase();
  const displayOverrides = { SA: 'KSA', AE: 'UAE' };
  return displayOverrides[normalized] || normalized;
};

export const getCountryName = (countryCode) => {
  const country = COUNTRIES.find(c => c.code === (countryCode || '').toUpperCase());
  return country ? country.name : (countryCode || '');
};

export const getNavButtonClasses = (currentPage, page, isMobile = false) => {
  const isActive = currentPage === page;
  const baseClasses = isMobile
    ? 'rounded-md px-3 py-2 text-left transition'
    : 'rounded-md px-3 py-2 text-sm font-medium transition-all duration-200';

  if (isActive) {
    return `${baseClasses} bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200 font-bold`;
  }

  return `${baseClasses} text-gray-600 hover:bg-indigo-50 hover:text-indigo-600`;
};

export const getLazyImageAttributes = () => ({
  loading: 'lazy',
  decoding: 'async',
  referrerPolicy: 'no-referrer',
  fetchPriority: 'low'
});

const LazyImage = ({ src, alt, className = '', wrapperClassName = 'bg-gray-200 animate-pulse', ...props }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!src) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    const node = imgRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [src]);

  if (!src) {
    return <div className={`w-full h-full ${wrapperClassName}`} aria-hidden="true" />;
  }

  if (!shouldLoad) {
    return <div ref={imgRef} className={`w-full h-full ${wrapperClassName}`} aria-label={alt || 'Loading image'} />;
  }

  return <img src={src} alt={alt} {...getLazyImageAttributes()} className={className} {...props} />;
};

const PROCEDURES = [
  'Knee Replacement', 'Hip Replacement', 'Bariatric Surgery', 'Lasik Eye Surgery',
  'Dental Implants', 'Cosmetic Rhinoplasty', 'Fertility Treatments', 'Cardiac Bypass',
  'Oncology', 'Neurology', 'Orthopedics', 'Dermatology'
];

const PRICE_RANGES = [
  { label: 'Low (<$5k)', min: 0, max: 5000 },
  { label: 'Mid ($5k - $15k)', min: 5001, max: 15000 },
  { label: 'High (>$15k)', min: 15001, max: 100000 }
];

// ==========================================
// MODULE 2: FIREBASE INIT
// ==========================================

const DEFAULT_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyAXOivlavWIXfbrzk8ngMzhgH3k9FYSeWU',
  authDomain: 'medinations-global-portal.firebaseapp.com',
  projectId: 'medinations-global-portal',
  storageBucket: 'medinations-global-portal.firebasestorage.app',
  messagingSenderId: '487145644708',
  appId: '1:487145644708:web:f78310f3419bc266897c31',
  measurementId: 'G-ZZ9240X46K'
};

const appId = process.env.REACT_APP_APP_ID || DEFAULT_FIREBASE_CONFIG.projectId;
let firebaseConfig = DEFAULT_FIREBASE_CONFIG;

const envConfig = process.env.REACT_APP_FIREBASE_CONFIG;
if (envConfig) {
  try {
    if (envConfig.trim().startsWith('{')) {
      firebaseConfig = JSON.parse(envConfig);
    } else {
      const decodedJson = atob(envConfig);
      firebaseConfig = JSON.parse(decodedJson);
    }
  } catch (e) {
    console.error('Config parse failed, using default.', e);
  }
}

let app, db, auth, storage;
try {
  if (Object.keys(firebaseConfig).length > 0 && firebaseConfig.apiKey) {
    app = initializeApp(firebaseConfig);
    if (typeof window !== 'undefined') {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider('6LfJTiQsAAAAAAoVmzWp26bILnhHQl-dwnfX-W6T'),
        isTokenAutoRefreshEnabled: true
      });
    }
    db = getFirestore(app);
    auth = getAuth(app);
    storage = getStorage(app);
  }
} catch (e) {
  console.error('App init failed:', e);
}

// Helpers
const getProvidersCollectionRef = (db) => collection(db, `artifacts/${appId}/public/data/providers`);
const getTourismOperatorsCollectionRef = (db) => collection(db, `artifacts/${appId}/public/data/tourismOperators`);

// ==========================================
// MODULE 3: SHARED UI COMPONENTS
// ==========================================

const WaveStyles = () => (
  <style>{`
    @keyframes wave {
      0% { transform: translateX(0) translateZ(0) scaleY(1) }
      50% { transform: translateX(-25%) translateZ(0) scaleY(0.55) }
      100% { transform: translateX(-50%) translateZ(0) scaleY(1) }
    }
    .wave-wrapper {
      position: absolute;
      width: 200%;
      height: 100%;
      bottom: 0;
      left: 0;
      opacity: 0.4;
      background-repeat: repeat no-repeat;
      background-position: 0 bottom;
      transform-origin: center bottom;
    }
    .wave-1 {
      background-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 800 100" xmlns="http://www.w3.org/2000/svg"><path d="M400 62c-73 0-112-25-200-25s-127 25-200 25v50h800v-50c-73 0-112-25-200-25s-127 25-200 25z" fill="%23ffffff" fill-opacity="0.4" /></svg>');
      background-size: 50% 100px;
      animation: wave 12s linear infinite;
      z-index: 1;
    }
    .wave-2 {
      background-image: url('data:image/svg+xml;utf8,<svg viewBox="0 0 800 100" xmlns="http://www.w3.org/2000/svg"><path d="M400 62c-73 0-112-25-200-25s-127 25-200 25v50h800v-50c-73 0-112-25-200-25s-127 25-200 25z" fill="%23ffffff" fill-opacity="0.6" /></svg>');
      background-size: 50% 120px;
      animation: wave 18s linear infinite;
      z-index: 2;
      bottom: -10px;
    }
  `}</style>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white p-6 rounded-xl shadow-md border border-gray-100 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, disabled = false, className = '', variant = 'primary', type = 'button' }) => {
  const variants = {
    primary: 'text-white bg-indigo-600 hover:bg-indigo-700',
    success: 'text-white bg-green-600 hover:bg-green-700',
    secondary: 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100',
    danger: 'text-white bg-red-600 hover:bg-red-700',
    outline: 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg transition disabled:opacity-50 ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
};

const Input = (props) => <input {...props} className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${props.className || ''}`} />;
const Select = (props) => <select {...props} className={`mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${props.className || ''}`}>{props.children}</select>;
const TextArea = (props) => <textarea {...props} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />;

const CanvasCaptcha = ({ onVerify }) => {
  const canvasRef = useRef(null);
  const [captchaCode, setCaptchaCode] = useState("");
  const [userInput, setUserInput] = useState("");
  const [isCorrect, setIsCorrect] = useState(false);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setCaptchaCode(code);
    setIsCorrect(false);
    onVerify(false);
    setUserInput("");
    return code;
  };

  const drawCaptcha = (code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f3f4f6";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for(let i=0; i<7; i++) {
        ctx.strokeStyle = `rgba(0,0,0,${Math.random() * 0.2})`;
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
    }
    ctx.font = "bold 24px Courier New";
    ctx.fillStyle = "#374151";
    ctx.setTransform(1, Math.random() * 0.2 - 0.1, Math.random() * 0.2 - 0.1, 1, 0, 0); 
    ctx.fillText(code, 20, 35);
    ctx.setTransform(1, 0, 0, 1, 0, 0); 
  };

  useEffect(() => { const code = generateCaptcha(); drawCaptcha(code); }, []);

  const handleChange = (e) => {
    const val = e.target.value.toUpperCase();
    setUserInput(val);
    if (val === captchaCode) { setIsCorrect(true); onVerify(true); } else { setIsCorrect(false); onVerify(false); }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
      <label className="block text-sm font-bold text-gray-700 mb-2">Security Check (Anti-Bot)</label>
      <div className="flex items-center space-x-3">
        <canvas ref={canvasRef} width="140" height="50" className="border border-gray-300 rounded cursor-pointer bg-gray-100" onClick={() => drawCaptcha(generateCaptcha())} title="Click to refresh" />
        <button type="button" onClick={() => drawCaptcha(generateCaptcha())} className="text-gray-500 hover:text-gray-700 text-sm">↻</button>
        <input type="text" value={userInput} onChange={handleChange} placeholder="ENTER CODE" className="w-40 px-3 py-2 border border-gray-300 rounded-md uppercase font-mono text-sm tracking-widest focus:ring-indigo-500" maxLength={6} />
        {isCorrect && <span className="text-green-600 font-bold">✓</span>}
      </div>
    </div>
  );
};

const ImageUploadControl = ({ label, storagePath, currentUrl, onUpdate, sizeHint = '800x600', aspectClass = 'aspect-video' }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fullPath = `${storagePath}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fullPath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onUpdate(url);
    } catch (err) { alert('Upload failed: ' + err.message); }
    setUploading(false);
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex justify-between items-end mb-1">
        <label className="block text-sm font-medium text-gray-700 truncate">{label}</label>
        <span className="text-xs text-gray-400 hidden sm:block">{sizeHint}</span>
      </div>
      <div className={`relative w-full ${aspectClass} bg-gray-100 rounded-lg overflow-hidden border border-gray-300 group flex-grow`}>
        {uploading ? <div className="absolute inset-0 flex items-center justify-center text-indigo-600 font-bold">Uploading...</div> : currentUrl ? (
          <LazyImage src={currentUrl} alt="Preview" className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-200" wrapperClassName="absolute inset-0 w-full h-full bg-gray-200 animate-pulse" />
        ) : <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">No Image</div>}
        <input type="file" accept="image/*" onChange={handleUpload} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" title="Click to upload" />
      </div>
    </div>
  );
};

// ==========================================
// MODULE 4: PAGE COMPONENTS
// ==========================================

// --- PAGE 1: PROVIDER PROFILE ---
const ProviderProfileSetup = ({ userId, db }) => {
  const initialProfile = {
    name: '', countryCode: '', countryProviderId: '', specialties: [], priceRangeLabel: '',
    website: '', phone: '', email: '', whatsapp: '', description: '', research: '', awards: '',
    images: ['', '', '', ''], isProvider: true, approved: false, address: '', aiAssistantEnabled: false 
  };
  const [profile, setProfile] = useState(initialProfile);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const getProviderIdLabel = () => {
      const country = COUNTRIES.find(c => c.code === profile.countryCode);
      return country ? `${country.idHint} *` : "National Provider ID / License *";
  };

  const updateImage = (index, url) => {
    const newImages = [...profile.images];
    newImages[index] = url;
    setProfile({ ...profile, images: newImages });
  };

  const handleSpecialtyChange = (e) => {
      const selected = Array.from(e.target.selectedOptions, option => option.value);
      if (selected.length > 5) { alert("Maximum 5 specialties allowed."); return; }
      setProfile({...profile, specialties: selected});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) { setMessage('Please complete the security check.'); return; }
    setLoading(true);
    try {
      await addDoc(collection(db, `artifacts/${appId}/public/data/providers`), {
          ...profile,
          ownerId: userId,
          approved: false,
          createdAt: new Date().toISOString()
      });
      setProfile(initialProfile);
      setSubmitted(true);
      setMessage('');
    } catch (err) { setMessage('Error: ' + err.message); }
    setLoading(false);
  };

  return (
    <div className="max-w-5xl mx-auto py-10 px-4">
      <Card>
        <div className="border-b pb-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Provider Registration</h2>
          <p className="text-sm text-gray-500">List your facility on Medinations.</p>
        </div>

        {submitted ? (
            <div className="p-8 rounded-lg text-center bg-green-50">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold mb-2 text-gray-800">Submission Received</h3>
                <p className="text-gray-600 mb-6">The provider profile has been submitted for review.</p>
                <Button variant="outline" className="max-w-xs mx-auto" onClick={() => setSubmitted(false)}>Add Another Provider</Button>
            </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div><label className="block text-sm font-medium text-gray-700">Clinic Name *</label><Input value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} required /></div>
             <div><label className="block text-sm font-medium text-gray-700">Country *</label><Select value={profile.countryCode} onChange={(e) => setProfile({...profile, countryCode: e.target.value})} required><option value="">Select</option>{COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}</Select></div>
             <div className="md:col-span-2">
                 <label className="block text-sm font-medium text-gray-700">Specialties (Hold Ctrl/Cmd to select up to 5) *</label>
                 <select multiple className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm h-32" value={profile.specialties} onChange={handleSpecialtyChange} required>
                    {PROCEDURES.map(p => <option key={p} value={p}>{p}</option>)}
                 </select>
                 <p className="text-xs text-gray-500 mt-1">Selected: {profile.specialties.join(", ")}</p>
             </div>
             <div><label className="block text-sm font-medium text-gray-700 text-indigo-700 font-bold">{getProviderIdLabel()}</label><Input value={profile.countryProviderId} onChange={(e) => setProfile({...profile, countryProviderId: e.target.value})} required placeholder="Enter License / Reg Number" /></div>
             <div><label className="block text-sm font-medium text-gray-700">Website</label><Input value={profile.website} onChange={(e) => setProfile({...profile, website: e.target.value})} /></div>
             <div><label className="block text-sm font-medium text-gray-700">Phone</label><Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} /></div>
             <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Physical Address *</label>
                <Input value={profile.address} onChange={(e) => setProfile({...profile, address: e.target.value})} required placeholder="Street, City, Postal Code" />
             </div>
          </div>
          <div className="space-y-4">
             <div><label className="block text-sm font-medium text-gray-700">Description (Mandatory) *</label><TextArea rows="4" placeholder="Describe your facility..." value={profile.description} onChange={(e) => setProfile({...profile, description: e.target.value})} required /></div>
             <div><label className="block text-sm font-medium text-gray-700">Specialty Research & Methodology</label><TextArea rows="3" placeholder="Proprietary techniques..." value={profile.research} onChange={(e) => setProfile({...profile, research: e.target.value})} /></div>
             <div><label className="block text-sm font-medium text-gray-700">Awards & Recognitions</label><Input placeholder="JCI, etc..." value={profile.awards} onChange={(e) => setProfile({...profile, awards: e.target.value})} /></div>
          </div>
          <div>
            <h3 className="text-lg font-medium text-indigo-700 mb-4">Facility Gallery</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 h-96">
               <div className="col-span-2 row-span-2"><ImageUploadControl label="Main Landscape" sizeHint="1200x800" storagePath={`providers/${userId}`} currentUrl={profile.images[0]} onUpdate={(url) => updateImage(0, url)} aspectClass="h-full w-full"/></div>
               <div className="col-span-1 row-span-2"><ImageUploadControl label="Vertical Shot" sizeHint="600x900" storagePath={`providers/${userId}`} currentUrl={profile.images[1]} onUpdate={(url) => updateImage(1, url)} aspectClass="h-full w-full"/></div>
               <div className="col-span-1"><ImageUploadControl label="Detail 1" sizeHint="600x600" storagePath={`providers/${userId}`} currentUrl={profile.images[2]} onUpdate={(url) => updateImage(2, url)} aspectClass="h-full w-full"/></div>
               <div className="col-span-1"><ImageUploadControl label="Detail 2" sizeHint="600x600" storagePath={`providers/${userId}`} currentUrl={profile.images[3]} onUpdate={(url) => updateImage(3, url)} aspectClass="h-full w-full"/></div>
            </div>
          </div>
          <div className="pt-6 border-t">
            <label className="block text-sm font-medium text-gray-700">Price Range</label>
            <Select value={profile.priceRangeLabel} onChange={(e) => setProfile({ ...profile, priceRangeLabel: e.target.value })} required className="w-1/2"><option value="">Select Price Tier</option>{PRICE_RANGES.map((r) => (<option key={r.label} value={r.label}>{r.label}</option>))}</Select>
          </div>
          <div className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <input
              id="ai-assistant-enabled"
              type="checkbox"
              checked={profile.aiAssistantEnabled}
              onChange={(e) => setProfile({ ...profile, aiAssistantEnabled: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="ai-assistant-enabled" className="text-sm text-gray-700">
              <span className="font-semibold text-indigo-700">Enable AI Patient Assistant</span>
              <span className="block text-gray-600 mt-1">Allow patients to ask questions about this provider. This is free for now.</span>
            </label>
          </div>
          <CanvasCaptcha onVerify={setCaptchaVerified} />
          <Button type="submit" disabled={loading || !captchaVerified} className="py-3 text-lg">{loading ? 'Submitting...' : 'Submit for Verification'}</Button>
          {message && <p className="text-center mt-2 font-medium text-green-600">{message}</p>}
        </form>
        )}
      </Card>
    </div>
  );
};

// --- PAGE 2: AGENT REGISTRATION ---
const AgentProfileSetup = ({ userId, db }) => {
  const initialAgentData = { name: '', country: '', bio: '', linkedin: '', email: '', photo: '', role: 'agent' };
  const [agentData, setAgentData] = useState(initialAgentData);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [statusToCheck, setStatusToCheck] = useState('checking'); 
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!userId || !db) return;
    const checkStatus = async () => {
        try {
            const adminRef = doc(db, 'admins', userId);
            const adminSnap = await getDoc(adminRef);
            if (adminSnap.exists()) setIsAdmin(true);

            // ONLY show status check if not admin.
            // If admin, we ALWAYS want a clean form, so skip this logic.
            if (!adminSnap.exists()) {
                const activeSnap = await getDoc(doc(db, 'agents', userId));
                if (activeSnap.exists()) {
                     setMessage('You are already an approved Agent.');
                     // DO NOT setAgentData here, keep it clean
                     setStatusToCheck('approved');
                     return;
                }
                const pendingSnap = await getDoc(doc(db, 'agent_applications', userId));
                if (pendingSnap.exists()) {
                    // DO NOT setAgentData here, keep it clean
                    setMessage('You have a pending application under review.');
                    setStatusToCheck('pending');
                    return;
                }
            }
            
            setStatusToCheck('none');
            
        } catch (e) {
            console.error(e);
            setStatusToCheck('none');
            setAgentData(initialAgentData); 
        }
    }
    checkStatus();
  }, [userId, db]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) { setMessage('Please complete the security check.'); return; }
    setLoading(true);
    try {
      if (isAdmin) {
          await addDoc(collection(db, 'agent_applications'), { ...agentData, submittedBy: userId, appliedAt: new Date().toISOString() });
          setMessage('New Agent Application created successfully.');
          setAgentData(initialAgentData); 
      } else {
          await setDoc(doc(db, 'agent_applications', userId), { ...agentData, id: userId, appliedAt: new Date().toISOString() }, { merge: true });
          setMessage('Application submitted! A Superadmin will review your profile shortly.');
          setStatusToCheck('pending');
      }
    } catch (err) { setMessage('Error: ' + err.message); }
    setLoading(false);
  };

  if (statusToCheck === 'checking') return <div className="p-10 text-center">Checking status...</div>;

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <Card>
        <div className="border-b pb-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Agent Registration</h2>
            {isAdmin && <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full ml-2">Admin Mode: Add New Agents</span>}
        </div>
        {(!isAdmin && (statusToCheck === 'pending' || statusToCheck === 'approved')) ? (
            <div className={`p-6 rounded-lg text-center ${statusToCheck === 'approved' ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}>
                <h3 className="text-xl font-bold mb-2">{statusToCheck === 'approved' ? '✅ You are a Verified Agent' : '⏳ Application Under Review'}</h3>
                {statusToCheck === 'approved' 
                  ? <p>You are an active agent. Please log into the Agent Portal for management tasks.</p> 
                  : <p>Your application is under review. You will be notified once verified.</p>
                }
            </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div><label className="block text-sm font-medium text-gray-700">Full Name</label><Input value={agentData.name} onChange={(e) => setAgentData({ ...agentData, name: e.target.value })} required /></div>
             <div><label className="block text-sm font-medium text-gray-700">Region</label><Select value={agentData.country} onChange={(e) => setAgentData({ ...agentData, country: e.target.value })} required><option value="">Select</option>{COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.name}</option>))}</Select></div>
          </div>
          <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo (Headshot)</label>
             <div className="w-40"><ImageUploadControl label="" sizeHint="400x400" storagePath={`agents/${userId}`} currentUrl={agentData.photo} onUpdate={(url) => setAgentData({...agentData, photo: url})} aspectClass="aspect-square" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700">Bio</label><TextArea rows="3" value={agentData.bio} onChange={(e) => setAgentData({ ...agentData, bio: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700">LinkedIn</label><Input type="url" value={agentData.linkedin} onChange={(e) => setAgentData({ ...agentData, linkedin: e.target.value })} /></div>
          <div><label className="block text-sm font-medium text-gray-700">Email</label><Input type="email" value={agentData.email} onChange={(e) => setAgentData({ ...agentData, email: e.target.value })} required /></div>
          <CanvasCaptcha onVerify={setCaptchaVerified} />
          <Button type="submit" disabled={loading || !captchaVerified} className="py-3">{loading ? 'Submitting...' : (isAdmin ? 'Register New Agent' : 'Submit Application')}</Button>
          {message && <div className={`mt-4 p-3 rounded text-center ${message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{message}</div>}
        </form>
        )}
      </Card>
    </div>
  );
};

// --- PAGE 3: TOURISM OPERATOR AD MANAGER ---
const TourismOperatorManager = ({ userId, db }) => {
  const initialAdState = { title: '', content: '', countryCode: '', operatorName: '', image: '', adminCode: '' };
  const [ad, setAd] = useState(initialAdState);
  const [message, setMessage] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaVerified) { setMessage('Please complete the security check.'); return; }
    try {
      await addDoc(collection(db, `artifacts/${appId}/public/data/tourismOperators`), { 
          ...ad, 
          ownerId: userId, 
          isApproved: false, 
          isAd: true,
          createdAt: new Date().toISOString()
      });
      setAd(initialAdState); 
      setSubmitted(true);
      setMessage('');
    } catch (err) { setMessage('Error: ' + err.message); }
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Post Travel Ad</h2>
          <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full">Operator Portal</span>
        </div>

        {submitted ? (
            <div className="p-8 rounded-lg text-center bg-green-50">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold mb-2 text-gray-800">Submission Received</h3>
                <p className="text-gray-600 mb-6">Your ad has been submitted for review.</p>
                <Button variant="outline" className="max-w-xs mx-auto" onClick={() => setSubmitted(false)}>Post Another Ad</Button>
            </div>
        ) : (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700">Operator Name</label><Input value={ad.operatorName} onChange={(e) => setAd({ ...ad, operatorName: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700">Headline</label><Input value={ad.title} onChange={(e) => setAd({ ...ad, title: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700">Target Country</label><Select value={ad.countryCode} onChange={(e) => setAd({ ...ad, countryCode: e.target.value })} required><option value="">Select</option>{COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}</Select></div>
            <div><label className="block text-sm font-medium text-gray-700">Content</label><TextArea rows="4" value={ad.content} onChange={(e) => setAd({ ...ad, content: e.target.value })} /></div>
          </div>
          <div><ImageUploadControl label="Poster (Vertical)" sizeHint="600x900" storagePath={`operators/${userId}`} currentUrl={ad.image} onUpdate={(url) => setAd({...ad, image: url})} aspectClass="aspect-[3/4]"/></div>
          <div className="md:col-span-2">
            <CanvasCaptcha onVerify={setCaptchaVerified} />
            <Button type="submit" className="py-3 mt-4" disabled={!captchaVerified}>Submit Ad</Button>
            {message && <p className="text-green-600 mt-2 text-center font-medium">{message}</p>}
          </div>
        </form>
        )}
      </Card>
    </div>
  );
};

// --- PAGE 4: AGENT DASHBOARD ---
const ComplianceReviewPage = ({ userId, db, allProviders, tourismOperators }) => {
  const [adminProfile, setAdminProfile] = useState(null);
  const [checking, setChecking] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('providers');
  const [agentApplications, setAgentApplications] = useState([]);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const verifyAdmin = async () => {
      if (!userId || !db) return;
      try {
        const adminSnap = await getDoc(doc(db, 'admins', userId));
        if (adminSnap.exists()) {
          setAdminProfile({ role: 'superadmin', ...adminSnap.data() });
        } else {
          const agentSnap = await getDoc(doc(db, 'agents', userId));
          if (agentSnap.exists()) setAdminProfile({ role: 'agent', ...agentSnap.data() });
        }
      } catch (e) { console.error(e); }
      setChecking(false);
    };
    verifyAdmin();
  }, [userId, db]);

  useEffect(() => {
    if (!db || !adminProfile || adminProfile.role !== 'superadmin') return;
    return onSnapshot(query(collection(db, 'agent_applications')), (s) => 
        setAgentApplications(s.docs.map(d => ({id: d.id, ...d.data()})))
    );
  }, [db, adminProfile]);

  const handleGoogleLogin = async () => { 
      setIsLoggingIn(true);
      try { await signInWithPopup(auth, new GoogleAuthProvider()); } 
      catch (e) { setMessage(e.message); }
      setIsLoggingIn(false);
  };

  const approveAgent = async (app) => {
      if (!window.confirm(`Approve ${app.name}?`)) return;
      await setDoc(doc(db, 'agents', app.id || app.name), { ...app, approvedAt: new Date().toISOString() });
      await deleteDoc(doc(db, 'agent_applications', app.id));
      setMessage('Agent Approved.');
  };

  const pendingProviders = useMemo(() => {
    if (!adminProfile) return [];
    return allProviders.filter(p => (adminProfile.role === 'superadmin' || p.countryCode === adminProfile.country));
  }, [allProviders, adminProfile]);

  const pendingAds = useMemo(() => {
    if (!adminProfile) return [];
    return tourismOperators.filter(p => (adminProfile.role === 'superadmin' || p.countryCode === adminProfile.country));
  }, [tourismOperators, adminProfile]);

  if (checking) return <div className="p-10 text-center text-gray-500">Verifying Access...</div>;

  return (
    <div className="max-w-6xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <h2 className="text-2xl font-bold text-gray-900">Agent Portal</h2>
        {!adminProfile ? (
            <button onClick={handleGoogleLogin} disabled={isLoggingIn} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded transition">
                {isLoggingIn ? 'Logging In...' : 'Agent Log In'}
            </button>
        ) : (
            <div className="text-right">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${adminProfile.role === 'superadmin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                    {adminProfile.role === 'superadmin' ? 'SUPERADMIN' : `AGENT: ${adminProfile.country}`}
                </span>
            </div>
        )}
      </div>
      
      {message && <div className="bg-green-100 text-green-800 p-2 mb-4 rounded text-center font-bold">{message}</div>}

      {!adminProfile ? (
          <div className="text-center py-20 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <div className="text-4xl mb-4">🔒</div>
              <h3 className="text-lg font-medium text-gray-700">Restricted Access</h3>
              <p className="text-gray-500 mt-2">Restricted to authorized agents only.</p>
          </div>
      ) : (
        <>
          <div className="flex space-x-6 mb-8 border-b border-gray-200 overflow-x-auto">
             <button onClick={() => setActiveTab('providers')} className={`pb-2 whitespace-nowrap ${activeTab === 'providers' ? 'border-b-2 border-indigo-600 font-bold' : 'text-gray-500'}`}>Providers ({pendingProviders.length})</button>
             <button onClick={() => setActiveTab('ads')} className={`pb-2 whitespace-nowrap ${activeTab === 'ads' ? 'border-b-2 border-orange-600 font-bold' : 'text-gray-500'}`}>Ads ({pendingAds.length})</button>
             {adminProfile.role === 'superadmin' && (
                 <button onClick={() => setActiveTab('agents')} className={`pb-2 whitespace-nowrap ${activeTab === 'agents' ? 'border-b-2 border-purple-600 font-bold' : 'text-gray-500'}`}>New Agent Apps ({agentApplications.length})</button>
             )}
          </div>

          {activeTab === 'providers' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingProviders.length === 0 && <div className="text-gray-400 p-4">No providers found for your region.</div>}
                {pendingProviders.map(p => (
                   <Card key={p.id}>
                      <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold">{p.name}</h4>
                            <p className="text-sm text-gray-500">{getCountryCodeLabel(p.countryCode)} | {p.specialties?.join(', ')}</p>
                            {p.approved ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Live</span> : <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Pending</span>}
                          </div>
                      </div>
                      <div className="mt-4 flex gap-2">
                         {!p.approved && (
                             <Button variant="success" className="py-1 text-xs" onClick={async () => {
                                 await setDoc(doc(getProvidersCollectionRef(db), p.id), { approved: true }, { merge: true });
                                 setMessage('Provider Approved');
                             }}>Approve</Button>
                         )}
                         <Button variant="danger" className="py-1 text-xs" onClick={async () => {
                             if(window.confirm('PERMANENTLY DELETE this provider? This cannot be undone.')) await deleteDoc(doc(getProvidersCollectionRef(db), p.id));
                         }}>Delete</Button>
                      </div>
                   </Card>
                ))}
             </div>
          )}

          {activeTab === 'ads' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingAds.length === 0 && <div className="text-gray-400 p-4">No ads found.</div>}
                {pendingAds.map(p => (
                   <Card key={p.id}>
                      <h4 className="font-bold">{p.title}</h4>
                      <p className="text-sm text-gray-500">{p.operatorName} ({getCountryCodeLabel(p.countryCode)})</p>
                      {p.isApproved ? <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Live</span> : <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Pending</span>}
                      <div className="mt-4 flex gap-2">
                         {!p.isApproved && (
                             <Button variant="success" className="py-1 text-xs" onClick={async () => {
                                 await setDoc(doc(getTourismOperatorsCollectionRef(db), p.id), { isApproved: true }, { merge: true });
                                 setMessage('Ad Approved');
                             }}>Approve</Button>
                         )}
                         <Button variant="danger" className="py-1 text-xs" onClick={async () => {
                             if(window.confirm('PERMANENTLY DELETE this ad?')) await deleteDoc(doc(getTourismOperatorsCollectionRef(db), p.id));
                         }}>Delete</Button>
                      </div>
                   </Card>
                ))}
             </div>
          )}

          {activeTab === 'agents' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agentApplications.length === 0 && <div className="text-gray-400 p-4">No new applications.</div>}
                {agentApplications.map(app => (
                   <Card key={app.id}>
                      <div className="flex items-center gap-4 mb-3">
                         {app.photo && <LazyImage src={app.photo} className="w-12 h-12 rounded-full object-cover" alt="" wrapperClassName="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />}
                         <div>
                            <h4 className="font-bold">{app.name}</h4>
                            <div className="text-sm text-gray-500">Applies for: <strong>{app.country}</strong></div>
                         </div>
                      </div>
                      <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded">{app.bio}</p>
                      <div className="mt-4 flex gap-2">
                         <Button variant="success" className="py-1 text-xs" onClick={() => approveAgent(app)}>Approve & Grant Access</Button>
                         <Button variant="danger" className="py-1 text-xs" onClick={async () => {
                             if(window.confirm('Reject?')) await deleteDoc(doc(db, 'agent_applications', app.id));
                         }}>Reject</Button>
                      </div>
                   </Card>
                ))}
             </div>
          )}
        </>
      )}
    </div>
  );
};

// --- NEW MODAL: TOUR OPERATOR DETAILS (Updated) ---
const TourOperatorDetailModal = ({ ad, onClose }) => {
    if (!ad) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 transition-opacity backdrop-blur-sm" onClick={onClose}></div>
            
            {/* Modal Content */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto flex flex-col">
                
                {/* Close Button (Floating) */}
                <button 
                    onClick={onClose} 
                    className="absolute top-3 right-3 z-10 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition"
                >
                    &times;
                </button>

                {/* Image Container - key fix: object-contain, max-height */}
                {ad.image && (
                    <div className="w-full bg-gray-100 flex items-center justify-center">
                        <LazyImage 
                            src={ad.image} 
                            alt={ad.title} 
                            className="w-full h-auto max-h-[60vh] object-contain" 
                            wrapperClassName="w-full h-auto max-h-[60vh] bg-gray-200 animate-pulse"
                        />
                    </div>
                )}

                {/* Text Content */}
                <div className="p-6">
                    <div className="flex flex-col mb-4 border-b border-gray-100 pb-4">
                        <h3 className="text-2xl font-bold text-gray-900 leading-tight">{ad.title}</h3>
                        <p className="text-indigo-600 font-bold mt-1 text-sm">{ad.operatorName} • {getCountryCodeLabel(ad.countryCode)}</p>
                    </div>
                    <div className="prose max-w-none text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                        {ad.content}
                    </div>
                    <button className="mt-6 w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 transition shadow-md">
                        Book This Package
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- AGENT DETAIL MODAL ---
const AgentDetailModal = ({ agent, onClose }) => {
    if (!agent) return null;
    const safeName = agent.name || 'Agent'; 
    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 relative">
                        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold">&times;</button>
                        <div className="flex flex-col items-center">
                            {agent.photo ? (
                                <LazyImage src={agent.photo} alt={safeName} className="w-24 h-24 rounded-full object-cover mb-4 border-4 border-indigo-50" wrapperClassName="w-24 h-24 rounded-full bg-gray-200 animate-pulse mb-4 border-4 border-indigo-50" />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center text-3xl font-bold text-indigo-600 mb-4">{safeName.charAt(0)}</div>
                            )}
                            <h3 className="text-xl font-bold text-gray-900">{safeName}</h3>
                            <span className="text-sm bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full mt-1 mb-4">{agent.country}</span>
                            <p className="text-gray-600 text-center text-sm">{agent.bio}</p>
                            
                            <div className="mt-6 w-full space-y-2">
                                <a href={`mailto:${agent.email}`} className="block w-full text-center py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">Contact Agent</a>
                                {agent.linkedin && (
                                    <a href={agent.linkedin} target="_blank" rel="noreferrer" className="block w-full text-center py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">View LinkedIn Profile</a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- AGENT LIST FOOTER ---
const GlobalAgentsFooter = ({ agents = [] }) => {
  const [selectedAgent, setSelectedAgent] = useState(null); 
  
  if (!agents || agents.length === 0) return null;
  return (
    <>
    <div className="mt-16 pt-8 border-t border-gray-200">
      <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Global Verification & Compliance Agents</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {agents.map((agent, idx) => {
            const safeName = agent.name || 'Agent'; 
            return (
              <div key={idx} onClick={() => setSelectedAgent(agent)} className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md cursor-pointer transition">
                {agent.photo ? (
                    <LazyImage src={agent.photo} alt={safeName} className="w-12 h-12 rounded-full object-cover" wrapperClassName="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                ) : (
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">{safeName.charAt(0)}</div>
                )}
                <div>
                  <div className="flex items-center">
                    <span className="font-bold text-gray-900 mr-2">{safeName}</span>
                    <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">{agent.country}</span>
                  </div>
                  <p className="text-sm text-gray-500 leading-tight mt-1 line-clamp-2">{agent.bio}</p>
                </div>
              </div>
            );
        })}
      </div>
    </div>
    {selectedAgent && <AgentDetailModal agent={selectedAgent} onClose={() => setSelectedAgent(null)} />}
    </>
  );
};

// --- PROVIDER DETAIL MODAL ---
const ProviderDetailModal = ({ provider, onClose }) => {
  if (!provider) return null;
  const imgs = provider.images || [];
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 relative">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold">&times;</button>
            <div className="sm:flex sm:items-start justify-between">
              <div>
                <h3 className="text-3xl leading-6 font-bold text-gray-900">{provider.name}</h3>
                <div className="mt-2 flex flex-wrap items-center space-x-3 text-sm text-gray-500">
                  <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-xs font-bold">{getCountryCodeLabel(provider.countryCode)}</span>
                  <span className="font-medium text-gray-700">{provider.priceRangeLabel}</span>
                  <div className="w-full mt-1 text-sm text-gray-500">
                      Specialties: <span className="font-medium text-indigo-600">{provider.specialties?.join(' • ') || provider.specialty1}</span>
                  </div>
                </div>
              </div>
              {provider.awards && <div className="mt-4 sm:mt-0 bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-1 rounded text-sm font-medium">🏆 {provider.awards}</div>}
            </div>
            <div className="mt-6 grid grid-cols-3 grid-rows-2 gap-2 h-96 w-full">
              <div className="col-span-2 row-span-1 bg-gray-100 rounded overflow-hidden relative">
                {imgs[0] && <LazyImage src={imgs[0]} className="w-full h-full object-cover" alt="Main" wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />}
              </div>
              <div className="col-span-1 row-span-2 bg-gray-100 rounded overflow-hidden relative">
                {imgs[1] && <LazyImage src={imgs[1]} className="w-full h-full object-cover" alt="Vertical" wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />}
              </div>
              <div className="col-span-1 row-span-1 bg-gray-100 rounded overflow-hidden relative">
                {imgs[2] && <LazyImage src={imgs[2]} className="w-full h-full object-cover" alt="Detail 1" wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />}
              </div>
              <div className="col-span-1 row-span-1 bg-gray-100 rounded overflow-hidden relative">
                {imgs[3] && <LazyImage src={imgs[3]} className="w-full h-full object-cover" alt="Detail 2" wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />}
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-6">
                <div><h4 className="text-xl font-bold text-gray-900 mb-2">About the Facility</h4><p className="text-gray-600 mt-1 whitespace-pre-wrap">{provider.description}</p></div>
                {provider.research && <div><h4 className="text-xl font-bold text-gray-900 mb-2">Research & Methodology</h4><p className="text-gray-600 mt-1 italic border-l-4 border-indigo-200 pl-3">{provider.research}</p></div>}
                
                {/* DISPLAY ADDRESS */}
                {provider.address && (
                   <div className="mt-4 p-4 border border-gray-200 rounded-lg">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Location</h4>
                      <p className="flex items-center text-gray-700">
                          <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                          {provider.address}
                      </p>
                   </div>
                )}
              </div>
              <div className="md:col-span-1 space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-3">Contact Provider</h4>
                  <div className="space-y-2 text-sm">
                    {provider.phone && <p className="flex items-center"><span className="w-6">📞</span> {provider.phone}</p>}
                    {provider.email && <p className="flex items-center"><span className="w-6">✉️</span> {provider.email}</p>}
                    {provider.website && <a href={provider.website} target="_blank" rel="noreferrer" className="flex items-center text-indigo-600 hover:underline">🌐 Website</a>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- USER SEARCH DASHBOARD ---
const buildProviderAssistantReply = (provider, question) => {
  if (!provider) return 'Please select a provider to continue.';
  const q = (question || '').toLowerCase();
  const countryName = getCountryName(provider.countryCode);
  const specialties = provider.specialties?.length ? provider.specialties.join(', ') : 'specialized care';
  const baseIntro = `${provider.name} is a healthcare provider in ${countryName}. It focuses on ${specialties}.`;

  if (q.includes('price') || q.includes('cost') || q.includes('fee') || q.includes('budget')) {
    const priceInfo = provider.priceRangeLabel || 'Please contact the provider for a custom quote.';
    return `${baseIntro} The listed pricing tier is ${priceInfo}. Final treatment costs depend on the care plan, length of stay, and required procedures. For exact pricing, please contact the clinic directly.`;
  }

  if (q.includes('location') || q.includes('where') || q.includes('address')) {
    return provider.address
      ? `${baseIntro} The facility is located at ${provider.address}.`
      : `${baseIntro} Please contact the clinic directly for exact location details.`;
  }

  if (q.includes('specialty') || q.includes('treatment') || q.includes('procedure')) {
    return `${baseIntro} Common treatment areas include ${specialties}. For a medical recommendation, the staff can confirm the best option based on your condition, medical history, and required care.`;
  }

  if (q.includes('doctor') || q.includes('team') || q.includes('staff')) {
    return `${provider.name} supports patient care through a multidisciplinary clinical team. The clinic can help with specialist matching, treatment coordination, and follow-up guidance.`;
  }

  return `${baseIntro} You may ask about pricing, treatment areas, location, or appointment coordination. For a personalised answer, the clinic can review your case and advise on the most suitable care pathway.`;
};

const ProviderAssistantPage = ({ providers = [] }) => {
  const enabledProviders = useMemo(() => (providers || []).filter(p => p.approved && p.aiAssistantEnabled), [providers]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');

  const filteredProviders = useMemo(() => {
    return enabledProviders.filter(p => !selectedCountry || p.countryCode === selectedCountry);
  }, [enabledProviders, selectedCountry]);

  useEffect(() => {
    if (!filteredProviders.length) {
      setSelectedProviderId('');
      return;
    }
    if (!filteredProviders.some(p => p.id === selectedProviderId)) {
      setSelectedProviderId(filteredProviders[0].id);
    }
  }, [filteredProviders, selectedProviderId]);

  const selectedProvider = filteredProviders.find(p => p.id === selectedProviderId) || filteredProviders[0] || null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProvider || !question.trim()) return;
    setAnswer(buildProviderAssistantReply(selectedProvider, question));
  };

  return (
    <div className="max-w-6xl mx-auto py-10 px-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Medinations AI Assistant</h2>
          <p className="text-gray-600 mt-1">Ask about a provider - This does not store any chat history.</p>
        </div>
        <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full">Ephemeral chat only</span>
      </div>

      {enabledProviders.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-600">
          No AI-enabled providers are currently live.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <aside className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
            >
              <option value="">All countries</option>
              {Array.from(new Set(enabledProviders.map(p => p.countryCode))).map(code => (
                <option key={code} value={code}>{getCountryName(code)}</option>
              ))}
            </select>

            <div className="mt-5 space-y-3">
              {filteredProviders.length === 0 ? (
                <div className="text-sm text-gray-500">No providers available for this country.</div>
              ) : (
                filteredProviders.map(provider => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => setSelectedProviderId(provider.id)}
                    className={`w-full text-left rounded-lg border p-3 transition ${selectedProvider?.id === provider.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 bg-white hover:border-indigo-300'}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-wide text-indigo-600">{getCountryCodeLabel(provider.countryCode)}</div>
                    <div className="font-semibold text-gray-900 mt-1">{provider.name}</div>
                  </button>
                ))
              )}
            </div>
          </aside>

          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            {selectedProvider ? (
              <>
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-indigo-600">{getCountryCodeLabel(selectedProvider.countryCode)}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mt-1">{selectedProvider.name}</h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">Ask the patient assistant</label>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows="5"
                    placeholder="Example: What treatments do you offer for fertility care?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium">
                    Ask Provider
                  </button>
                </form>

                {answer && (
                  <div className="mt-6 rounded-xl bg-indigo-50 border border-indigo-100 p-4 text-gray-700">
                    <div className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-2">Assistant response</div>
                    <p className="whitespace-pre-line leading-relaxed">{answer}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-gray-500 py-12 text-center">Select a provider to start.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserSearchDashboard = ({ allProviders, tourismOperators, approvedAgents }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null); // State for Tour Operator Modal

  const activeProviders = useMemo(() => {
    return allProviders.filter(p => p.approved).filter(p => {
        const term = searchTerm.toLowerCase();
        const matchesTerm = !searchTerm || 
            p.name?.toLowerCase().includes(term) || 
            (p.specialties && p.specialties.some(s => s.toLowerCase().includes(term))) ||
            p.specialty1?.toLowerCase().includes(term);
        
        const matchesCountry = !searchCountry || p.countryCode === searchCountry;
        return matchesTerm && matchesCountry;
    });
  }, [allProviders, searchTerm, searchCountry]);

  const activeAds = useMemo(() => tourismOperators.filter(op => op.isApproved && op.isAd), [tourismOperators]);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4">
      {/* Wave Styles Injection */}
      <WaveStyles /> 
      
      <div className="bg-gradient-to-r from-indigo-600 to-blue-500 rounded-3xl p-10 mb-10 text-center text-white shadow-xl relative overflow-hidden">
        {/* Wave Animation Layers */}
        <div className="wave-wrapper">
            <div className="wave-1"></div>
            <div className="wave-2"></div>
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold mb-8 text-shadow-sm">Find World-Class Healthcare</h2>
          
          {/* SEARCH BAR CONTAINER */}
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center bg-white rounded-2xl sm:rounded-full shadow-2xl p-2 sm:p-1 gap-2 sm:gap-0">
            
            {/* COUNTRY DROPDOWN - MOBILE STACKED / DESKTOP PILL */}
            <div className="relative w-full sm:w-1/3 border-b sm:border-b-0 sm:border-r border-gray-200">
                <select 
                    className="appearance-none w-full bg-transparent px-6 py-3 text-gray-700 font-medium cursor-pointer focus:outline-none text-center sm:text-left"
                    value={searchCountry}
                    onChange={(e) => setSearchCountry(e.target.value)}
                >
                    <option value="">All Countries</option>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
                {/* Custom Chevron for Mobile & Desktop */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </div>
            </div>

            {/* SEARCH INPUT */}
            <div className="relative w-full sm:w-2/3 flex items-center">
                <div className="absolute left-4 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
                <input
                  type="text"
                  placeholder="Search specialty, procedure..."
                  className="w-full pl-12 pr-4 py-3 text-gray-900 outline-none bg-transparent rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="hidden sm:block absolute right-1 top-1 bottom-1 bg-indigo-600 hover:bg-indigo-700 text-white px-8 rounded-full font-bold transition shadow-md sm:shadow-none">Search</button>
            </div>
            
            {/* MOBILE ONLY SEARCH BUTTON */}
            <button className="sm:hidden w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold transition shadow-md mt-1">Search Now</button>

          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
        <div className="lg:col-span-3 space-y-6">
           <h3 className="text-xl font-bold text-gray-800">Verified Hospitals ({activeProviders.length})</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeProviders.map(p => (
                 <div key={p.id} className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md cursor-pointer" onClick={() => setSelectedProvider(p)}>
                    <div className="h-48 bg-gray-200 relative">
                       {p.images && p.images[0] && <LazyImage src={p.images[0]} className="w-full h-full object-cover" alt={p.name} wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />}
                       <div className="absolute top-2 right-2 bg-white/90 text-indigo-700 text-xs font-bold px-2 py-1 rounded">{getCountryCodeLabel(p.countryCode)}</div>
                    </div>
                    <div className="p-5">
                       <h4 className="font-bold text-lg">{p.name}</h4>
                       <p className="text-sm text-indigo-600 truncate">{p.specialties?.join(', ') || p.specialty1}</p>
                    </div>
                 </div>
              ))}
           </div>
        </div>
        <div className="lg:col-span-1 space-y-6">
           <h3 className="text-xl font-bold text-gray-800">Travel Packages</h3>
           {activeAds.map(ad => (
              <div 
                  key={ad.id} 
                  className="group relative bg-white rounded-xl shadow-md overflow-hidden border aspect-[3/4] cursor-pointer hover:shadow-lg transition"
                  onClick={() => setSelectedAd(ad)} // OPEN AD MODAL
              >
                 {ad.image ? (
                     <LazyImage src={ad.image} className="w-full h-full object-cover transition duration-700 group-hover:scale-105" alt={ad.title} wrapperClassName="w-full h-full bg-gray-200 animate-pulse" />
                 ) : (
                     <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">No Image</div>
                 )}
                 {/* HIDDEN OVERLAY: Only shows on hover, minimal info, no blocking text */}
                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition duration-300"></div>
              </div>
           ))}
        </div>
      </div>

      <GlobalAgentsFooter agents={approvedAgents} />
      {selectedProvider && <ProviderDetailModal provider={selectedProvider} onClose={() => setSelectedProvider(null)} />}
      {selectedAd && <TourOperatorDetailModal ad={selectedAd} onClose={() => setSelectedAd(null)} />}
    </div>
  );
};

// ==========================================
// MAIN APP
// ==========================================

const App = () => {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [page, setPage] = useState('dashboard');
  const [userId, setUserId] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [allProviders, setAllProviders] = useState([]);
  const [tourismOperators, setTourismOperators] = useState([]);
  const [approvedAgents, setApprovedAgents] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    if (!auth || !authUser) return;
    try {
      await signOut(auth);
      setAuthUser(null);
      setUserId(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    if (!auth) return;
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUserId(u.uid);
        setAuthUser(u.isAnonymous ? null : u);
      } else {
        setAuthUser(null);
        setUserId(null);
        try {
          await signInAnonymously(auth);
        } catch (error) {
          console.warn("Anonymous auth failed (likely browser privacy settings):", error);
        }
      }
      setIsAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!db) return;
    const unsub1 = onSnapshot(query(getProvidersCollectionRef(db)), (s) => setAllProviders(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsub2 = onSnapshot(query(getTourismOperatorsCollectionRef(db)), (s) => setTourismOperators(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsub3 = onSnapshot(collection(db, 'agents'), (s) => setApprovedAgents(s.docs.map(d => {
        const data = d.data();
        return { id: d.id, ...data, name: data.name || 'Agent' };
    })));
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  const renderPage = () => {
    if (!isAuthReady) return <div className="h-screen flex items-center justify-center text-indigo-600">Loading...</div>;
    switch (page) {
      case 'provider': return <ProviderProfileSetup userId={userId} db={db} />;
      case 'operator': return <TourismOperatorManager userId={userId} db={db} />;
      case 'assistant': return <ProviderAssistantPage providers={allProviders} />;
      case 'agent-signup': return <AgentProfileSetup userId={userId} db={db} />;
      case 'agent': return <ComplianceReviewPage userId={userId} db={db} allProviders={allProviders} tourismOperators={tourismOperators} />;
      default: return <UserSearchDashboard allProviders={allProviders} tourismOperators={tourismOperators} approvedAgents={approvedAgents} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <nav className="bg-white shadow sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => setPage('dashboard')}>
            <span className="font-bold text-2xl text-indigo-600 mr-2">medi<span className="text-gray-900">N</span>ations<span className="text-gray-400 text-lg">.com</span></span>
            <span className="hidden md:inline-block text-sm text-gray-500 italic border-l border-gray-300 pl-3">Healthcare Without Borders</span>
          </div>
          <div className="hidden md:flex items-center space-x-2">
            <button
              onClick={() => setPage('dashboard')}
              aria-current={page === 'dashboard' ? 'page' : undefined}
              className={getNavButtonClasses(page, 'dashboard')}
            >
              Find Treatment
            </button>
            <button
              onClick={() => setPage('provider')}
              aria-current={page === 'provider' ? 'page' : undefined}
              className={getNavButtonClasses(page, 'provider')}
            >
              Providers
            </button>
            <button
              onClick={() => setPage('operator')}
              aria-current={page === 'operator' ? 'page' : undefined}
              className={getNavButtonClasses(page, 'operator')}
            >
              Travel Operators
            </button>
            <button
              onClick={() => setPage('assistant')}
              aria-current={page === 'assistant' ? 'page' : undefined}
              className={getNavButtonClasses(page, 'assistant')}
            >
              AI Assistant
            </button>
            <button
              onClick={() => setPage('agent-signup')}
              aria-current={page === 'agent-signup' ? 'page' : undefined}
              className={getNavButtonClasses(page, 'agent-signup')}
            >
              Join as Agent
            </button>
            <button
              onClick={() => setPage('agent')}
              aria-current={page === 'agent' ? 'page' : undefined}
              className={page === 'agent' ? 'rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-600 transition-all duration-200 hover:bg-indigo-100' : 'rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-indigo-50 hover:text-indigo-600'}
            >
              Agent Portal
            </button>
            {authUser && (
              <div className="ml-2 flex items-center gap-2 rounded-full bg-white px-2.5 py-1.5 shadow-sm ring-1 ring-gray-100">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                  {(authUser.displayName || authUser.email || 'U').charAt(0).toUpperCase()}
                </div>
                <span className="max-w-[140px] truncate text-sm font-medium text-gray-700">
                  {authUser.displayName || authUser.email || 'User'}
                </span>
                <button
                  type="button"
                  onClick={handleLogout}
                  aria-label="Log out"
                  title="Log out"
                  className="text-gray-500 transition hover:text-red-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <path d="M16 17l5-5-5-5" />
                    <path d="M21 12H9" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="md:hidden flex items-center gap-2">
             {authUser && (
               <button
                 type="button"
                 onClick={handleLogout}
                 aria-label="Log out"
                 title="Log out"
                 className="text-gray-500 hover:text-red-600"
               >
                 <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                   <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                   <path d="M16 17l5-5-5-5" />
                   <path d="M21 12H9" />
                 </svg>
               </button>
             )}
             <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>☰</button>
          </div>
        </div>
        {isMobileMenuOpen && (
           <div className="md:hidden bg-white border-t p-4 flex flex-col gap-2">
              <button
                onClick={() => {setPage('dashboard'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'dashboard' ? 'page' : undefined}
                className={getNavButtonClasses(page, 'dashboard', true)}
              >
                Find Treatment
              </button>
              <button
                onClick={() => {setPage('provider'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'provider' ? 'page' : undefined}
                className={getNavButtonClasses(page, 'provider', true)}
              >
                Providers
              </button>
              <button
                onClick={() => {setPage('operator'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'operator' ? 'page' : undefined}
                className={getNavButtonClasses(page, 'operator', true)}
              >
                Travel Operators
              </button>
              <button
                onClick={() => {setPage('assistant'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'assistant' ? 'page' : undefined}
                className={getNavButtonClasses(page, 'assistant', true)}
              >
                AI Assistant
              </button>
              <button
                onClick={() => {setPage('agent-signup'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'agent-signup' ? 'page' : undefined}
                className={getNavButtonClasses(page, 'agent-signup', true)}
              >
                Join as Agent
              </button>
              <button
                onClick={() => {setPage('agent'); setIsMobileMenuOpen(false)}}
                aria-current={page === 'agent' ? 'page' : undefined}
                className={page === 'agent' ? 'rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-left font-bold text-indigo-600 transition hover:bg-indigo-100' : 'rounded-md px-3 py-2 text-left text-gray-600 transition hover:bg-indigo-50 hover:text-indigo-600'}
              >
                Agent Portal
              </button>
           </div>
        )}
      </nav>
      <main>{renderPage()}</main>
    </div>
  );
};

export default App;